import { useQuery } from '@tanstack/react-query';
import {
  BUSY_STATUSES,
  importsApi,
  type EntityType,
  type ImportJob,
  type RowView,
} from '@/api/imports.api';
import { useCan } from '@/hooks/useCan';
import { ActionsEnum } from '@/lib/permissions';

/**
 * Query keys under `import-jobs`: the realtime feed announces every job
 * change as entity `import_job`, which the invalidation map's fallback turns
 * into this root — so a progress bar moves without polling while the socket
 * is up. The intervals below are the fallback for when it is not.
 */
export const importKeys = {
  all: ['import-jobs'] as const,
  list: (entity: EntityType, page: number) =>
    ['import-jobs', 'list', entity, page] as const,
  active: () => ['import-jobs', 'active'] as const,
  job: (id: string) => ['import-jobs', 'job', id] as const,
  rows: (id: string, view: RowView, page: number, q: string) =>
    ['import-jobs', 'rows', id, view, page, q] as const,
  presets: (entity: EntityType) => ['import-jobs', 'presets', entity] as const,
  rollback: (id: string) => ['import-jobs', 'rollback', id] as const,
  source: (patientId: string) => ['import-jobs', 'source', patientId] as const,
};

export const isBusy = (job?: ImportJob | null) =>
  !!job && BUSY_STATUSES.includes(job.status);

/**
 * Importing writes records in bulk: an owner-level right (`clinic:manage`),
 * plus the right to the records themselves — patients, or finance for
 * income and expenses.
 */
export function useCanImport(entity: EntityType = 'patients') {
  const { can } = useCan();
  return (
    can('clinic', ActionsEnum.MANAGE) &&
    can(entity === 'patients' ? 'patients' : 'finance', ActionsEnum.MANAGE)
  );
}

export function useImportJob(id: string | undefined) {
  return useQuery({
    queryKey: importKeys.job(id ?? ''),
    queryFn: () => importsApi.get(id!),
    enabled: !!id,
    refetchInterval: (q) => (isBusy(q.state.data) ? 2000 : false),
  });
}

export function useImportRows(
  id: string | undefined,
  view: RowView,
  page: number,
  pageSize: number,
  q: string,
  enabled = true,
) {
  return useQuery({
    queryKey: importKeys.rows(id ?? '', view, page, q),
    queryFn: () => importsApi.rows(id!, view, page, pageSize, q),
    enabled: !!id && enabled,
    placeholderData: (prev) => prev,
  });
}

export function useActiveImports(enabled: boolean) {
  return useQuery({
    queryKey: importKeys.active(),
    queryFn: () => importsApi.active(),
    enabled,
    refetchInterval: (q) =>
      q.state.data?.items.some((j) => isBusy(j)) ? 3000 : 60_000,
  });
}
